import { ChangeEvent, forwardRef, useCallback, useMemo } from "react";

import { LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { Body } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";

import { useId } from "../../hooks/useId";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { useSubmitting } from "../SubmitGroup";
import { useValidity } from "../ValidityGroup";
import { InputContainer } from "../shared";
import { CharacterCount } from "../shared/CharacterCount";
import { normalizeAutocompleteValue } from "../utils/autocompleteHelpers";
import { Placeholder } from "./Placeholder";
import { StyledAutoSizer } from "./StyledAutoSizer";
import { StyledNativeInput } from "./StyledNativeInput";
import { TextAreaProps } from "./TextArea";

import styles from "./styles.module.scss";

/**
 * The `Input` component properties.
 */
export interface InputProps extends TextAreaProps {
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;
}

/**
 * The base input for text areas.
 */
export const Input = forwardRef<HTMLTextAreaElement, InputProps>(function Input(props, ref) {
  const {
    label,
    instructions,
    tooltip,
    hideInstructions,
    hideLabel,
    defaultValue = null,
    required = false,
    autoFocus,
    autoSize = false,
    lines,
    placeholder,
    maxLength,
    tabIndex,
    onChange,
    onFocusEnter,
    onFocusExit,
    autoComplete,
  } = props;

  const { value = defaultValue } = props;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.TextArea,
      "text-area",
      {
        "fixed-height": lines !== undefined,
        "auto-size": autoSize,
      },
    ],
    testIdPrefix: "text-area",
  });

  const id = useId();
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();
  const [invalid] = useValidity();

  const changeHandler = useCallback(
    (ev: ChangeEvent<HTMLTextAreaElement>) => {
      if (disabled) return;
      if (readOnly || submitting) return;

      onChange?.(ev.target.value, value);
    },
    [value, disabled, readOnly, submitting, onChange],
  );

  const focusHandler = useCallback(() => {
    onFocusEnter?.();
  }, [onFocusEnter]);

  const blurHandler = useCallback(() => {
    onFocusExit?.();
  }, [onFocusExit]);

  const textValue = value ?? "";

  const characterCount = useMemo(() => value?.length, [value]) || 0;

  const autocompleteValue = useMemo(() => normalizeAutocompleteValue(autoComplete), [autoComplete]);

  return (
    <LoadingGroup.Placeholder
      fallback={<Placeholder className={props.className} style={props.style} lines={lines} hasInstructions={!!instructions} />}
    >
      <InputContainer
        {...getBasePropsFromHtmlProps(baseHtmlProps)}
        id={id}
        label={label}
        instructions={
          (!!instructions || !!maxLength) && (
            <CharacterCount maxLength={maxLength} characterCount={characterCount}>
              {instructions}
            </CharacterCount>
          )
        }
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        tooltip={tooltip}
        hideInstructions={hideInstructions}
        hideLabel={hideLabel}
      >
        <Body className="input-container">
          <StyledNativeInput
            id={id}
            aria-labelledby={`${id}-label`}
            aria-invalid={invalid}
            aria-required={disabled || readOnly ? undefined : required}
            aria-disabled={disabled || submitting}
            aria-readonly={readOnly}
            aria-describedby={instructions ? `${id}-instructions` : undefined}
            aria-errormessage={invalid ? `${id}-error` : undefined}
            aria-busy={submitting}
            className={classNames("input", { invalid, "no-scroll": autoSize })}
            disabled={disabled}
            autoFocus={autoFocus}
            readOnly={readOnly || submitting}
            ref={ref}
            tabIndex={disabled || readOnly || submitting ? -1 : (tabIndex ?? 0)}
            rows={lines}
            placeholder={placeholder}
            value={textValue}
            autoComplete={autocompleteValue}
            onChange={changeHandler}
            onFocus={focusHandler}
            onBlur={blurHandler}
          />
          {autoSize && <StyledAutoSizer>{`${textValue} `}</StyledAutoSizer>}
        </Body>
      </InputContainer>
    </LoadingGroup.Placeholder>
  );
});
